import React, { useState, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import aiAutomationApi from '../utils/aiAutomationApi';

/**
 * ChangeMailPage — Dev utility to update candidate email addresses after sourcing.
 *
 * Flow:
 *   1. User enters a session ID → clicks "Load Candidates"
 *   2. Displays session name + table of candidates (name, email)
 *   3. User can edit the email inline and save per candidate
 *   4. "Clear" button resets the page
 */
function ChangeMailPage() {
  const { isDark } = useTheme();
  const { settings } = useSettings();
  const primaryColor = settings?.primaryColor || '#3B5BDB';

  const [sessionId, setSessionId] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  // Track which candidate rows have been edited (candidateId -> newEmail)
  const [editedEmails, setEditedEmails] = useState({});
  // Track saving state per candidate
  const [savingIds, setSavingIds] = useState(new Set());

  // ── Load candidates by session ID ──────────────────────────────────
  const loadCandidates = useCallback(async () => {
    const trimmed = sessionId.trim();
    if (!trimmed) {
      setError('Please enter a session ID');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMsg('');
    setCandidates([]);
    setSessionName('');
    setEditedEmails({});

    try {
      const data = await aiAutomationApi.get(`/candidates/by-session/${encodeURIComponent(trimmed)}`);
      if (!data || data.status !== 'success') {
        throw new Error('Invalid response from server');
      }

      const candidatesList = Array.isArray(data.candidates) ? data.candidates : [];

      setSessionName(data.session_name || trimmed);
      setCandidates(candidatesList);
      if (candidatesList.length === 0) {
        setError('No candidates found for this session.');
      }
    } catch (err) {
      setError(err.message || 'Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // ── Handle email field change ──────────────────────────────────────
  const handleEmailChange = (candidateId, value) => {
    setEditedEmails((prev) => ({ ...prev, [candidateId]: value }));
  };

  // ── Save updated email for a single candidate ─────────────────────
  const saveEmail = useCallback(async (candidateId) => {
    const newEmail = (editedEmails[candidateId] ?? '').trim();
    if (!newEmail) {
      setError('Email cannot be empty');
      return;
    }

    setSavingIds((prev) => new Set(prev).add(candidateId));
    setError('');
    setSuccessMsg('');

    try {
      const response = await aiAutomationApi.put('/candidates/update-email', {
        candidate_id: candidateId,
        new_email: newEmail,
      });

      // Update local state
      setCandidates((prev) =>
        prev.map((c) => (c.id === candidateId ? { ...c, email: newEmail } : c))
      );
      setEditedEmails((prev) => {
        const copy = { ...prev };
        delete copy[candidateId];
        return copy;
      });
      // Use message from backend response (contains candidate name)
      setSuccessMsg(response?.message || `Email updated successfully`);
    } catch (err) {
      setError(err.message || 'Failed to update email');
    } finally {
      setSavingIds((prev) => {
        const copy = new Set(prev);
        copy.delete(candidateId);
        return copy;
      });
    }
  }, [editedEmails]);

  // ── Clear / reset everything ───────────────────────────────────────
  const handleClear = () => {
    setSessionId('');
    setSessionName('');
    setCandidates([]);
    setEditedEmails({});
    setError('');
    setSuccessMsg('');
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Change Candidate Email</h1>
          <button
            onClick={handleClear}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDark
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            Clear
          </button>
        </div>

        {/* Session ID input */}
        <div className="flex gap-3 mb-6">
          <input
            type="text"
            placeholder="Enter Session ID..."
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadCandidates()}
            className={`flex-1 px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors ${
              isDark
                ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500 focus:border-blue-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
            }`}
          />
          <button
            onClick={loadCandidates}
            disabled={loading}
            style={{ backgroundColor: primaryColor }}
            className="px-5 py-2.5 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load Candidates'}
          </button>
        </div>

        {/* Error / Success messages */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 p-3 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm">
            {successMsg}
          </div>
        )}

        {/* Session Name */}
        {sessionName && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${isDark ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
            <span className="font-medium">Session:</span> {sessionName}
          </div>
        )}

        {/* Candidates Table */}
        {candidates.length > 0 && (
          <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? 'bg-gray-800' : 'bg-gray-100'}>
                  <th className="text-left px-4 py-3 font-medium w-12">#</th>
                  <th className="text-left px-4 py-3 font-medium">Name</th>
                  <th className="text-left px-4 py-3 font-medium">Email</th>
                  <th className="text-left px-4 py-3 font-medium w-24">Action</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c, idx) => {
                  const isEdited = editedEmails[c.id] !== undefined;
                  const isSaving = savingIds.has(c.id);
                  const displayEmail = isEdited ? editedEmails[c.id] : c.email;

                  return (
                    <tr
                      key={c.id}
                      className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} ${
                        isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3">{c.name}</td>
                      <td className="px-4 py-3">
                        <input
                          type="email"
                          value={displayEmail}
                          onChange={(e) => handleEmailChange(c.id, e.target.value)}
                          className={`w-full px-3 py-1.5 rounded border text-sm outline-none transition-colors ${
                            isDark
                              ? 'bg-gray-800 border-gray-600 text-gray-100 focus:border-blue-400'
                              : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                          } ${isEdited ? (isDark ? 'border-yellow-500' : 'border-yellow-400') : ''}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => saveEmail(c.id)}
                          disabled={!isEdited || isSaving}
                          style={isEdited && !isSaving ? { backgroundColor: primaryColor } : {}}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-opacity ${
                            isEdited && !isSaving
                              ? 'text-white hover:opacity-90'
                              : isDark
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChangeMailPage;
